/**
 * Portal Manta · TipoObraMatrix
 * Extraído de App.jsx v29.3 (via AST)
 */
import React, { useState, useEffect, useMemo, useCallback, Fragment, memo } from 'react';
import { V, S, FonteBadge, OBRAS_ROD } from '../../globals.js';

function TipoObraMatrix({lote, onFonte}){
  const lk = lote.k;
  const obras = OBRAS_ROD[lk] || {};
  // Agregar por rodovia
  const sumByRod = (arr, key='km') => {
    const m = {};
    (arr||[]).forEach(x=>{ const k = x.rod||x.nome||''; if(!k) return; m[k] = (m[k]||0) + (x[key]||0); });
    return m;
  };
  const dup = sumByRod(obras.duplicacoes, 'km');
  const fai = sumByRod(obras.faixas, 'km');
  const mar = sumByRod(obras.marginais, 'km');
  const cic = sumByRod(obras.ciclovias, 'km');
  const oae = sumByRod(obras.oaes, 'n');
  const pas = sumByRod(obras.passarelas, 'n');
  const dia = sumByRod(obras.diamantes, 'n');
  const tro = sumByRod(obras.trombetas, 'n');
  const par = sumByRod(obras.parclos, 'n');
  const tre = sumByRod(obras.trevos, 'n');
  const rot = sumByRod(obras.rotatorias, 'n');
  // Lista única de rodovias (ordenadas pela extensão decrescente)
  const sis = obras.sistema || [];
  const rods = [...new Set([
    ...sis.map(x=>x.rod),
    ...Object.keys(dup), ...Object.keys(fai), ...Object.keys(mar), ...Object.keys(cic),
    ...Object.keys(oae), ...Object.keys(pas), ...Object.keys(dia), ...Object.keys(tro), ...Object.keys(par), ...Object.keys(tre), ...Object.keys(rot),
  ])].filter(Boolean);
  // Ordena por extensão (sistema)
  const extMap = {}; sis.forEach(x=>{extMap[x.rod]=x.ext||0;});
  rods.sort((a,b)=>(extMap[b]||0)-(extMap[a]||0));

  // Contornos vão como rodovias separadas
  const cont = sumByRod(obras.contornos, 'km');

  const fmtCel = (n,d=1)=> (n==null||n===0)?'—': Number(n).toLocaleString('pt-BR',{minimumFractionDigits:d,maximumFractionDigits:d});

  // Totais
  const T = {dup:0,fai:0,mar:0,cic:0,oae:0,pas:0,int:0};
  rods.forEach(r=>{ T.dup+=dup[r]||0; T.fai+=fai[r]||0; T.mar+=mar[r]||0; T.cic+=cic[r]||0; T.oae+=oae[r]||0; T.pas+=pas[r]||0; T.int+=(dia[r]||0)+(tro[r]||0)+(par[r]||0)+(tre[r]||0)+(rot[r]||0); });

  const fonteMatriz = {doc:'PER + OBRAS_ROD interno', pag:'Agregação trecho-a-trecho do PER por rodovia', data:lote.data_base||'—', aut:'Manta · Engenharia'};

  return (<div style={{...S.cardP, marginBottom:16, padding:'16px 18px'}}>
    <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:10}}>
      <div style={{fontSize:12,fontWeight:700,color:V.br}}>Matriz · Rodovia × Tipo de obra (físico)</div>
      <FonteBadge fonte={fonteMatriz} onClick={()=>onFonte({fonte:fonteMatriz,label:'Matriz rodovia × tipo de obra'})}/>
    </div>
    <div style={{fontSize:10.5,color:V.g4,marginBottom:10}}>Quantidades físicas por rodovia. km para obras lineares · un para pontuais.</div>
    <div style={{overflowX:'auto'}}>
      <table style={{width:'100%',borderCollapse:'collapse',fontSize:10.5}}>
        <thead>
          <tr style={{background:V.g0,borderBottom:'2px solid '+V.g2}}>
            <th style={{padding:'7px 8px',textAlign:'left',fontWeight:700,color:V.g5,fontSize:9.5}}>Rodovia</th>
            <th style={{padding:'7px 8px',textAlign:'right',fontWeight:700,color:V.g5,fontSize:9.5}}>Ext (km)</th>
            <th style={{padding:'7px 8px',textAlign:'right',fontWeight:700,color:V.or,fontSize:9.5}}>Dup (km)</th>
            <th style={{padding:'7px 8px',textAlign:'right',fontWeight:700,color:'#CD853F',fontSize:9.5}}>Faixa (km)</th>
            <th style={{padding:'7px 8px',textAlign:'right',fontWeight:700,color:'#A0826D',fontSize:9.5}}>Margi (km)</th>
            <th style={{padding:'7px 8px',textAlign:'right',fontWeight:700,color:V.gn,fontSize:9.5}}>Ciclo (km)</th>
            <th style={{padding:'7px 8px',textAlign:'right',fontWeight:700,color:'#5D3A1A',fontSize:9.5}}>OAE (un)</th>
            <th style={{padding:'7px 8px',textAlign:'right',fontWeight:700,color:'#8B4A2D',fontSize:9.5}}>Pass (un)</th>
            <th style={{padding:'7px 8px',textAlign:'right',fontWeight:700,color:V.bl,fontSize:9.5}}>Inter (un)</th>
          </tr>
        </thead>
        <tbody>
          {rods.map((r,i)=>{
            const intT = (dia[r]||0)+(tro[r]||0)+(par[r]||0)+(tre[r]||0)+(rot[r]||0);
            const ext = extMap[r];
            return (<tr key={i} style={{borderBottom:'1px solid '+V.g1}}>
              <td style={{padding:'7px 8px',fontWeight:700,color:V.br}}>{r}</td>
              <td style={{padding:'7px 8px',textAlign:'right',fontFamily:'monospace',color:V.g5}}>{fmtCel(ext)}</td>
              <td style={{padding:'7px 8px',textAlign:'right',fontFamily:'monospace',color:dup[r]?V.or:V.g4,fontWeight:dup[r]?700:400}}>{fmtCel(dup[r])}</td>
              <td style={{padding:'7px 8px',textAlign:'right',fontFamily:'monospace',color:fai[r]?'#CD853F':V.g4,fontWeight:fai[r]?700:400}}>{fmtCel(fai[r])}</td>
              <td style={{padding:'7px 8px',textAlign:'right',fontFamily:'monospace',color:mar[r]?'#A0826D':V.g4,fontWeight:mar[r]?700:400}}>{fmtCel(mar[r])}</td>
              <td style={{padding:'7px 8px',textAlign:'right',fontFamily:'monospace',color:cic[r]?V.gn:V.g4,fontWeight:cic[r]?700:400}}>{fmtCel(cic[r])}</td>
              <td style={{padding:'7px 8px',textAlign:'right',fontFamily:'monospace',color:oae[r]?'#5D3A1A':V.g4,fontWeight:oae[r]?700:400}}>{fmtCel(oae[r],0)}</td>
              <td style={{padding:'7px 8px',textAlign:'right',fontFamily:'monospace',color:pas[r]?'#8B4A2D':V.g4,fontWeight:pas[r]?700:400}}>{fmtCel(pas[r],0)}</td>
              <td style={{padding:'7px 8px',textAlign:'right',fontFamily:'monospace',color:intT?V.bl:V.g4,fontWeight:intT?700:400}}>{fmtCel(intT,0)}</td>
            </tr>);
          })}
          {Object.keys(cont).length>0 && Object.entries(cont).map(([nome,km],i)=>(
            <tr key={'cont'+i} style={{borderBottom:'1px solid '+V.g1, background:V.orp}}>
              <td style={{padding:'7px 8px',fontWeight:700,color:V.br2,fontStyle:'italic'}}>↳ {nome}</td>
              <td colSpan={8} style={{padding:'7px 8px',color:V.g5,fontSize:10}}>contorno novo · {fmtCel(km)} km de implantação</td>
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr style={{background:V.g0,borderTop:'2px solid '+V.g2,fontWeight:700}}>
            <td style={{padding:'7px 8px',color:V.br}}>Total</td>
            <td style={{padding:'7px 8px',textAlign:'right',fontFamily:'monospace',color:V.br}}>{fmtCel(rods.reduce((s,r)=>s+(extMap[r]||0),0))}</td>
            <td style={{padding:'7px 8px',textAlign:'right',fontFamily:'monospace',color:V.or}}>{fmtCel(T.dup)}</td>
            <td style={{padding:'7px 8px',textAlign:'right',fontFamily:'monospace',color:'#CD853F'}}>{fmtCel(T.fai)}</td>
            <td style={{padding:'7px 8px',textAlign:'right',fontFamily:'monospace',color:'#A0826D'}}>{fmtCel(T.mar)}</td>
            <td style={{padding:'7px 8px',textAlign:'right',fontFamily:'monospace',color:V.gn}}>{fmtCel(T.cic)}</td>
            <td style={{padding:'7px 8px',textAlign:'right',fontFamily:'monospace',color:'#5D3A1A'}}>{fmtCel(T.oae,0)}</td>
            <td style={{padding:'7px 8px',textAlign:'right',fontFamily:'monospace',color:'#8B4A2D'}}>{fmtCel(T.pas,0)}</td>
            <td style={{padding:'7px 8px',textAlign:'right',fontFamily:'monospace',color:V.bl}}>{fmtCel(T.int,0)}</td>
          </tr>
        </tfoot>
      </table>
    </div>
    <div style={{marginTop:10,padding:'8px 10px',background:V.g0,borderRadius:6,fontSize:10.5,color:V.g5,lineHeight:1.5}}>
      <strong>Como ler:</strong> a coluna mais "alta" por linha aponta o tipo de obra que define aquela rodovia. Linhas com várias colunas pesadas indicam rodovias com perfil misto (ex: PR-323 no L4 — duplicação + interseções + passarelas).
    </div>
  </div>);
}

export { TipoObraMatrix };
export default TipoObraMatrix;
